import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../layouts/Dashboard";
import { $authHost, meningitisMonitoringCRUD } from "../../http";
import { toLocalCalendarDateString } from "../../functions/dateHelpers";
import FilterDatePicker from "../../components/DatePicker/FilterDatePiecker";
import { Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";

const PCR_FIELDS = [
    "budgetPcrLiquor",
    "budgetPcrSmear",
    "budgetPcrPositive",
    "paidPcrLiquor",
    "paidPcrSmear",
    "paidPcrPositive",
];

const tableWrapStyle = {
    width: "100%",
    overflowX: "auto",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "#fff",
};

const tableStyle = {
    width: "max-content",
    minWidth: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
};

const thStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    background: "#fafafa",
    border: "1px solid #e0e0e0",
    padding: "6px 8px",
    whiteSpace: "nowrap",
    fontWeight: 600,
    textAlign: "center",
    verticalAlign: "middle",
};

const tdStyle = {
    border: "1px solid #eee",
    padding: "4px 6px",
    verticalAlign: "middle",
    textAlign: "center",
};

const inputStyle = {
    width: 64,
    padding: "4px 6px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    fontSize: 12,
    textAlign: "center",
};

const formatApiDate = (value) => {
    if (!value) return "";
    const ymd = String(value).slice(0, 10);
    const parts = ymd.split("-");
    if (parts.length !== 3) return String(value);
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

const parseApiDate = (value) => {
    if (!value) return null;
    const ymd = String(value).slice(0, 10);
    const [year, month, day] = ymd.split("-").map(Number);
    if (!year || !month || !day) return new Date(value);
    return new Date(year, month - 1, day);
};

const formatPercent = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "";
    const number = Number(value);
    return Number.isInteger(number) ? String(number) : number.toFixed(2);
};

const percent = (numerator, denominator) => {
    if (numerator == null || denominator == null || denominator === 0) return null;
    return Math.round((100 * numerator * 100) / denominator) / 100;
};

const sameNumber = (left, right) => {
    const leftEmpty = left === "" || left == null;
    const rightEmpty = right === "" || right == null;
    if (leftEmpty && rightEmpty) return true;
    if (leftEmpty || rightEmpty) return false;
    return Number(left) === Number(right);
};

const draftsFromRows = (rows) => {
    const drafts = {};
    (rows || []).forEach((row) => {
        drafts[row.key] = {};
        PCR_FIELDS.forEach((field) => {
            drafts[row.key][field] = row[field] ?? null;
        });
    });
    return drafts;
};

const isDirtyRow = (row, draft) =>
    PCR_FIELDS.some((field) => !sameNumber(draft?.[field], row[field]));

const parseManual = (value, label) => {
    if (value === "" || value == null) return null;
    const number = Number(value);
    if (!Number.isFinite(number) || !Number.isInteger(number) || number < 0) {
        throw new Error(`${label}: укажите целое неотрицательное число`);
    }
    return number;
};

const validateBlock = (liquor, smear, positive, title) => {
    const total = (liquor ?? 0) + (smear ?? 0);
    if (positive != null && positive > total) {
        throw new Error(`${title}: число положительных не может превышать сумму ПЦР ликвор + мазок`);
    }
};

const errorMessage = (error) => {
    if (!error) return "Произошла ошибка";
    if (typeof error === "string") return error;
    return error.message || "Произошла ошибка";
};

const filenameFromDisposition = (header, fallback) => {
    if (!header) return fallback;
    const utf = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf) {
        try {
            return decodeURIComponent(utf[1]);
        } catch (e) {
            return fallback;
        }
    }
    const ascii = header.match(/filename="?([^";]+)"?/i);
    return ascii ? ascii[1] : fallback;
};

const NumberCell = ({ value }) => (
    <td className="ant-table-cell" style={tdStyle}>
        {value == null ? "" : value}
    </td>
);

const PercentCell = ({ value }) => (
    <td className="ant-table-cell" style={tdStyle}>
        {formatPercent(value)}
    </td>
);

const ManualInput = ({ value, disabled, onChange }) => (
    <td className="ant-table-cell" style={tdStyle}>
        {disabled ? (
            value == null ? "" : value
        ) : (
            <input
                type="number"
                min={0}
                step={1}
                value={value ?? ""}
                disabled={disabled}
                onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === "") {
                        onChange(null);
                        return;
                    }
                    const number = Number(raw);
                    onChange(Number.isInteger(number) ? number : raw);
                }}
                onWheel={(event) => event.currentTarget.blur()}
                style={inputStyle}
            />
        )}
    </td>
);

const MeningitisMonitoring = () => {
    const [periodDate, setPeriodDate] = useState(() => new Date());
    const [week, setWeek] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [rowAction, setRowAction] = useState({});

    const periodStartParam = toLocalCalendarDateString(periodDate);
    const loadedPeriodStart = week?.periodStart
        ? String(week.periodStart).slice(0, 10)
        : periodStartParam;

    const fetchWeek = async (date = periodDate, silent = false) => {
        if (!silent) setLoading(true);
        try {
            const query = toLocalCalendarDateString(date);
            const data = await meningitisMonitoringCRUD.get(
                query ? `week?periodStart=${encodeURIComponent(query)}` : "week"
            );
            setWeek(data || null);
            setDrafts(draftsFromRows(data?.rows));
            const monday = parseApiDate(data?.periodStart);
            if (monday) setPeriodDate(monday);
        } catch (error) {
            console.error(error);
            alert(errorMessage(error) || "Не удалось сформировать отчёт");
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchWeek();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateDraft = (rowKey, field, value) => {
        setDrafts((prev) => ({
            ...prev,
            [rowKey]: {
                ...(prev[rowKey] || {}),
                [field]: value,
            },
        }));
    };

    const pcrPayload = (row) => {
        const draft = drafts[row.key] || {};
        const budgetPcrLiquor = parseManual(draft.budgetPcrLiquor, "Бюджет, ПЦР ликвор");
        const budgetPcrSmear = parseManual(draft.budgetPcrSmear, "Бюджет, ПЦР мазок");
        const budgetPcrPositive = parseManual(draft.budgetPcrPositive, "Бюджет, положительные");
        const paidPcrLiquor = parseManual(draft.paidPcrLiquor, "Платные, ПЦР ликвор");
        const paidPcrSmear = parseManual(draft.paidPcrSmear, "Платные, ПЦР мазок");
        const paidPcrPositive = parseManual(draft.paidPcrPositive, "Платные, положительные");
        validateBlock(budgetPcrLiquor, budgetPcrSmear, budgetPcrPositive, "Бюджет");
        validateBlock(paidPcrLiquor, paidPcrSmear, paidPcrPositive, "Платные");
        return {
            budgetPcrLiquor,
            budgetPcrSmear,
            budgetPcrPositive,
            paidPcrLiquor,
            paidPcrSmear,
            paidPcrPositive,
        };
    };

    const ensureRowId = async (row) => {
        if (row.id) return row.id;
        const params = new URLSearchParams();
        if (loadedPeriodStart) params.set("periodStart", loadedPeriodStart);
        if (row.filialId) params.set("filialId", String(row.filialId));
        const created = await meningitisMonitoringCRUD.get(`current?${params.toString()}`);
        if (!created?.id) throw new Error("Не удалось создать отчёт филиала");
        return created.id;
    };

    const saveRow = async (row) => {
        const payload = pcrPayload(row);
        if (row.id) {
            await meningitisMonitoringCRUD.edit({ id: row.id, ...payload });
            return;
        }
        await meningitisMonitoringCRUD.create({
            periodStart: loadedPeriodStart,
            filialId: row.filialId,
            ...payload,
        });
    };

    const handleSave = async (row) => {
        setRowAction((prev) => ({ ...prev, [row.key]: "save" }));
        try {
            await saveRow(row);
            await fetchWeek(parseApiDate(week?.periodStart) || periodDate, true);
        } catch (error) {
            console.error(error);
            alert(errorMessage(error));
        } finally {
            setRowAction((prev) => ({ ...prev, [row.key]: null }));
        }
    };

    const handleClose = async (row) => {
        if (!window.confirm("После закрытия редактирование будет недоступно. Закрыть отчёт филиала?")) {
            return;
        }
        setRowAction((prev) => ({ ...prev, [row.key]: "close" }));
        try {
            if (isDirtyRow(row, drafts[row.key])) {
                await saveRow(row);
            }
            const id = await ensureRowId(row);
            await meningitisMonitoringCRUD.post("close", { id });
            await fetchWeek(parseApiDate(week?.periodStart) || periodDate, true);
        } catch (error) {
            console.error(error);
            alert(errorMessage(error));
        } finally {
            setRowAction((prev) => ({ ...prev, [row.key]: null }));
        }
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const response = await $authHost.get("/api/v1/meningitis-monitoring/export", {
                params: periodStartParam ? { periodStart: periodStartParam } : {},
                responseType: "blob",
            });
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const stampStart = week?.periodStart ? String(week.periodStart).slice(0, 10).replace(/-/g, "") : "";
            const stampEnd = week?.periodEnd ? String(week.periodEnd).slice(0, 10).replace(/-/g, "") : "";
            const fallback = `Мониторинг_менингит_${stampStart}_${stampEnd}.xlsx`;
            const fileName = filenameFromDisposition(response.headers?.["content-disposition"], fallback);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            if (error?.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    alert(json.message || json.title || text);
                } catch (parseError) {
                    alert("Ошибка при выгрузке Excel");
                }
            } else {
                alert(errorMessage(error) || "Ошибка при выгрузке Excel");
            }
        } finally {
            setExportLoading(false);
        }
    };

    const rows = week?.rows || [];
    const totals = week?.totals;
    const periodLabel = week
        ? `${formatApiDate(week.periodStart)} — ${formatApiDate(week.periodEnd)}`
        : "";

    const computedPercents = useMemo(() => {
        const map = {};
        rows.forEach((row) => {
            const draft = drafts[row.key] || {};
            const budgetLiquor = draft.budgetPcrLiquor == null ? null : Number(draft.budgetPcrLiquor);
            const budgetSmear = draft.budgetPcrSmear == null ? null : Number(draft.budgetPcrSmear);
            const budgetPositive = draft.budgetPcrPositive == null ? null : Number(draft.budgetPcrPositive);
            const paidLiquor = draft.paidPcrLiquor == null ? null : Number(draft.paidPcrLiquor);
            const paidSmear = draft.paidPcrSmear == null ? null : Number(draft.paidPcrSmear);
            const paidPositive = draft.paidPcrPositive == null ? null : Number(draft.paidPcrPositive);
            map[row.key] = {
                budgetPcrPercent: percent(
                    Number.isInteger(budgetPositive) ? budgetPositive : null,
                    (Number.isInteger(budgetLiquor) ? budgetLiquor : 0) +
                        (Number.isInteger(budgetSmear) ? budgetSmear : 0)
                ),
                paidPcrPercent: percent(
                    Number.isInteger(paidPositive) ? paidPositive : null,
                    (Number.isInteger(paidLiquor) ? paidLiquor : 0) +
                        (Number.isInteger(paidSmear) ? paidSmear : 0)
                ),
            };
        });
        return map;
    }, [rows, drafts]);

    return (
        <DashboardLayout>
            <Stack spacing={1.5}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
                    Мониторинг менингит
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Исследования на менингит за период{periodLabel ? `: ${periodLabel}` : ""}. Неделя —
                    понедельник–воскресенье. Бактериол. метод и «полож» подтягиваются из тарификатора,
                    ПЦР заполняется вручную. После закрытия строка филиала не редактируется.
                </Typography>

                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                    <label style={{ minWidth: 160 }}>Отчётная неделя</label>
                    <FilterDatePicker
                        label="дата недели"
                        value={periodDate}
                        handleFunction={(value) => {
                            if (value) setPeriodDate(value);
                        }}
                        style={{ width: "220px" }}
                    />
                    <LoadingButton loading={loading} variant="contained" onClick={() => fetchWeek(periodDate)}>
                        Сформировать
                    </LoadingButton>
                    <LoadingButton
                        loading={exportLoading}
                        variant="outlined"
                        color="success"
                        onClick={handleExport}
                    >
                        Скачать Excel
                    </LoadingButton>
                </Stack>
            </Stack>

            <div style={{ height: 12 }} />

            <div style={tableWrapStyle}>
                <table style={tableStyle} className="ant-table">
                    <thead className="ant-table-thead">
                        <tr>
                            <th rowSpan={3} style={thStyle}>№</th>
                            <th rowSpan={3} style={{ ...thStyle, minWidth: 280 }}>Филиалы</th>
                            <th colSpan={7} style={thStyle}>Бюджет</th>
                            <th colSpan={7} style={thStyle}>Платные</th>
                            <th rowSpan={3} style={thStyle}>Действия</th>
                        </tr>
                        <tr>
                            <th rowSpan={2} style={thStyle}>Бактериол. метод</th>
                            <th rowSpan={2} style={thStyle}>полож</th>
                            <th rowSpan={2} style={thStyle}>уд. вес (%)</th>
                            <th style={thStyle}>ПЦР</th>
                            <th style={thStyle}>ПЦР</th>
                            <th rowSpan={2} style={thStyle}>полож</th>
                            <th rowSpan={2} style={thStyle}>уд. вес (%)</th>
                            <th rowSpan={2} style={thStyle}>Бактериол. метод</th>
                            <th rowSpan={2} style={thStyle}>полож</th>
                            <th rowSpan={2} style={thStyle}>уд. вес (%)</th>
                            <th style={thStyle}>ПЦР</th>
                            <th style={thStyle}>ПЦР</th>
                            <th rowSpan={2} style={thStyle}>полож</th>
                            <th rowSpan={2} style={thStyle}>уд. вес (%)</th>
                        </tr>
                        <tr>
                            <th style={thStyle}>ликвор</th>
                            <th style={thStyle}>мазок</th>
                            <th style={thStyle}>ликвор</th>
                            <th style={thStyle}>мазок</th>
                        </tr>
                    </thead>
                    <tbody className="ant-table-tbody">
                        {rows.map((row) => {
                            const draft = drafts[row.key] || {};
                            const dirty = isDirtyRow(row, draft);
                            const busy = Boolean(rowAction[row.key]);
                            const isCurrent = week?.currentFilialId && row.filialId === week.currentFilialId;
                            const showClose = !row.isClosed && (row.canClose || (row.canEdit && row.filialId));
                            const status = row.isClosed ? "Закрыт" : row.id ? "Черновик" : "";
                            return (
                                <tr
                                    key={row.key}
                                    className="ant-table-row"
                                    style={isCurrent ? { backgroundColor: "#f6ffed" } : undefined}
                                >
                                    <td className="ant-table-cell" style={tdStyle}>{row.ordinal}</td>
                                    <td
                                        className="ant-table-cell"
                                        style={{
                                            ...tdStyle,
                                            textAlign: "left",
                                            whiteSpace: "normal",
                                            minWidth: 280,
                                            maxWidth: 420,
                                        }}
                                    >
                                        {row.filialName}
                                        {status ? (
                                            <div style={{ color: row.isClosed ? "#8c8c8c" : "#237804", fontSize: 11 }}>
                                                {status}
                                            </div>
                                        ) : null}
                                    </td>
                                    <NumberCell value={row.budgetBactQuantity} />
                                    <NumberCell value={row.budgetBactPositive} />
                                    <PercentCell value={row.budgetBactPercent} />
                                    <ManualInput
                                        value={draft.budgetPcrLiquor}
                                        disabled={!row.canEdit || busy}
                                        onChange={(value) => updateDraft(row.key, "budgetPcrLiquor", value)}
                                    />
                                    <ManualInput
                                        value={draft.budgetPcrSmear}
                                        disabled={!row.canEdit || busy}
                                        onChange={(value) => updateDraft(row.key, "budgetPcrSmear", value)}
                                    />
                                    <ManualInput
                                        value={draft.budgetPcrPositive}
                                        disabled={!row.canEdit || busy}
                                        onChange={(value) => updateDraft(row.key, "budgetPcrPositive", value)}
                                    />
                                    <PercentCell value={computedPercents[row.key]?.budgetPcrPercent ?? row.budgetPcrPercent} />
                                    <NumberCell value={row.paidBactQuantity} />
                                    <NumberCell value={row.paidBactPositive} />
                                    <PercentCell value={row.paidBactPercent} />
                                    <ManualInput
                                        value={draft.paidPcrLiquor}
                                        disabled={!row.canEdit || busy}
                                        onChange={(value) => updateDraft(row.key, "paidPcrLiquor", value)}
                                    />
                                    <ManualInput
                                        value={draft.paidPcrSmear}
                                        disabled={!row.canEdit || busy}
                                        onChange={(value) => updateDraft(row.key, "paidPcrSmear", value)}
                                    />
                                    <ManualInput
                                        value={draft.paidPcrPositive}
                                        disabled={!row.canEdit || busy}
                                        onChange={(value) => updateDraft(row.key, "paidPcrPositive", value)}
                                    />
                                    <PercentCell value={computedPercents[row.key]?.paidPcrPercent ?? row.paidPcrPercent} />
                                    <td className="ant-table-cell" style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                                        {row.canEdit && (
                                            <LoadingButton
                                                size="small"
                                                loading={rowAction[row.key] === "save"}
                                                disabled={busy || !dirty}
                                                onClick={() => handleSave(row)}
                                            >
                                                Сохранить
                                            </LoadingButton>
                                        )}
                                        {showClose && (
                                            <LoadingButton
                                                size="small"
                                                color="warning"
                                                loading={rowAction[row.key] === "close"}
                                                disabled={busy}
                                                onClick={() => handleClose(row)}
                                            >
                                                Закрыть
                                            </LoadingButton>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {totals && rows.length > 0 && (
                            <tr className="ant-table-row" style={{ backgroundColor: "#d9f7be", fontWeight: 600 }}>
                                <td className="ant-table-cell" style={tdStyle} />
                                <td className="ant-table-cell" style={{ ...tdStyle, textAlign: "left" }}>ИТОГО</td>
                                <NumberCell value={totals.budgetBactQuantity} />
                                <NumberCell value={totals.budgetBactPositive} />
                                <PercentCell value={totals.budgetBactPercent} />
                                <NumberCell value={totals.budgetPcrLiquor} />
                                <NumberCell value={totals.budgetPcrSmear} />
                                <NumberCell value={totals.budgetPcrPositive} />
                                <PercentCell value={totals.budgetPcrPercent} />
                                <NumberCell value={totals.paidBactQuantity} />
                                <NumberCell value={totals.paidBactPositive} />
                                <PercentCell value={totals.paidBactPercent} />
                                <NumberCell value={totals.paidPcrLiquor} />
                                <NumberCell value={totals.paidPcrSmear} />
                                <NumberCell value={totals.paidPcrPositive} />
                                <PercentCell value={totals.paidPcrPercent} />
                                <td className="ant-table-cell" style={tdStyle} />
                            </tr>
                        )}
                        {!rows.length && !loading && (
                            <tr>
                                <td
                                    className="ant-table-cell"
                                    style={{ ...tdStyle, whiteSpace: "normal", padding: 16 }}
                                    colSpan={18}
                                >
                                    Нажмите «Сформировать», чтобы загрузить данные за выбранную неделю.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </DashboardLayout>
    );
};

export default MeningitisMonitoring;
